import { Component, ViewEncapsulation, ChangeDetectorRef, Inject, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { GISService } from 'app/shared/services/gis-service';
import { GisServiceList, ServiceTypes } from 'app/shared/models/gis-service-list';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { UiUtilsService } from '../../../shared/utils/ui-utils.service';
import { Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { GIS_SERVICE_URL_PATTERN, REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { customerEditorQueryParam } from 'app/shared/models/customer';
import { DomOperationUtilsService } from 'app/shared/utils/dom-operation-utils.service';
import { TrackBy } from 'app/shared/utils/track-by';

type AuthMethodName = 'basic' | 'custom';

interface AuthMethod {
    name: AuthMethodName;
    text: string;
}

interface ServiceType {
    name: ServiceTypes;
    text: string;
}

@Component({
    selector: 'ads-prism-gis-add-edit',
    templateUrl: './ads-prism-gis-add-edit.component.html',
    styleUrls: ['./ads-prism-gis-add-edit.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdsPrismGISAddEditComponent implements OnDestroy {

    public translations = {
        WARNING: '',
        URL_IS_NOT_HTTPS: '',
        OK: '',
        CANCEL: ''
    }

    public hasBaseDropZoneOver: boolean;
    public hasError: boolean;
    public errorMessage: string;
    public cid: string;
    public uploading: boolean;
    public editMode: boolean;
    public validated = false;
    public gisService: GisServiceList;
    public gisServiceurl: string;
    public gisServiceappid: string;
    public gisServiceAuthurl: string;
    public gisServiceauth: string = '';
    public gisServicetype: ServiceTypes;
    public gisServicemethod: AuthMethodName = null;
    public gisServiceusername: string = '';
    public gisServicepassword: string = '';

    public urlPattern = REGEX_CONFIG.urlPattern;
    public gisServicetypeEnumList = ServiceTypes;

    public get gisServiceUrlPattern(): string {
        return this.gisServicetype === ServiceTypes.MAPS || this.gisServicetype === ServiceTypes.FEATURE
            ? GIS_SERVICE_URL_PATTERN
            : this.urlPattern;
    }

    public servicetypelist: ServiceType[] = [
        { name: ServiceTypes.IDENTITY, text: 'ArcGIS Identity' },
        { name: ServiceTypes.FEATURE, text: 'FeatureServer' },
        { name: ServiceTypes.MAPS, text: 'MapServer' },
    ];

    public authMethodlist: AuthMethod[] = [
        { name: 'basic', text: 'BASIC' },
        { name: 'custom', text: 'CUSTOM' },
    ];
    @ViewChild('servicePassword') public el: ElementRef;

    public customerId: number = 0;
    private subscriptions: Subscription[] = [];
    public trackByName = TrackBy.byUniqueKey<{name: string; text: string}>()('name');

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: GisServiceList,
        public refDialog: MatDialogRef<AdsPrismGISAddEditComponent>,
        public cdr: ChangeDetectorRef,
        public translateService: TranslateService,
        public activatedRoute: ActivatedRoute,
        public GISService: GISService,
        public uiUitlsService: UiUtilsService,
        public matDialog: MatDialog,
        private domOperationUtilsService: DomOperationUtilsService
    ) {
        this.subscriptions.push(
            this.activatedRoute.queryParamMap.subscribe((params: ParamMap) => {
                this.customerId = Number(params.get(customerEditorQueryParam));
                this.setData();
            }),
        );

        this.translations.WARNING = this.translateService.instant('GIS_ADD_EDIT.WARNING');
        this.translations.URL_IS_NOT_HTTPS = this.translateService.instant('GIS_ADD_EDIT.URL_IS_NOT_HTTPS');
        this.translations.OK = this.translateService.instant('GIS_ADD_EDIT.OK');
        this.translations.CANCEL = this.translateService.instant('GIS_ADD_EDIT.CANCEL');

        this.subscriptions.push(
            this.domOperationUtilsService.gisDialogAuthMethod.subscribe((method: 'basic' | 'custom') => this.gisServicemethod = method)
        );
    }

    /**
     * Closing the Popup window
     */
    public close(servicesaved: boolean) {
        this.refDialog.close({ success: servicesaved });
    }

    /**
     * setting editing mode and Data for editing
     */
    public setData() {
        if (this.data) {
            this.gisService = this.data;
            this.gisServicetype = this.gisService.serviceType;
            this.gisServiceurl = this.gisService.url;
            this.gisServiceappid = this.gisService.appId;
            this.gisServiceAuthurl = this.gisService.authURL;
            this.gisServicemethod = (this.gisService.authMethod as AuthMethodName);
            this.domOperationUtilsService.gisDialogAuthMethod.next(this.gisServicemethod);
            this.gisServiceusername = this.gisService.userName;
            this.gisServicepassword = this.gisService.password;
            this.gisServiceauth = this.gisService.authParams;
            this.editMode = true;
        }
        if (this.data?.hasIdentityConfig && this.gisServicetype !== ServiceTypes.IDENTITY) {
            this.servicetypelist = this.servicetypelist.filter((type) => type.name !== ServiceTypes.IDENTITY);
        }
    }

    public uiSave() {
        if ((this.gisServiceurl && this.gisServiceurl.trim().startsWith('https://')) || this.gisServicetype === ServiceTypes.IDENTITY) {
            this.doSave();
        } else {
            this.matDialog
                .open(ConfirmationDialogComponent, {
                    disableClose: true,
                    data: {
                        title: this.translations.WARNING,
                        message: this.translations.URL_IS_NOT_HTTPS,
                        okText: this.translations.OK,
                        cancelText: this.translations.CANCEL,
                    },
                })
                .afterClosed()
                .subscribe((result) => {
                    if (result.whichButtonWasPressed === 'ok') {
                        this.doSave();
                    }
                });
        }
    }

    /**
     * Saving service Data to Server
     */
    public doSave() {
        if (this.editMode) {
            this.gisService.serviceType = this.gisServicetype;
            this.gisService.url = this.gisServiceurl;
            this.gisService.appId = this.gisServiceappid;
            this.gisService.authURL = this.gisServiceAuthurl;
            this.gisService.customerId = this.customerId;
            this.gisService.authparam = this.gisServiceauth;
            this.gisService.authMethod = this.gisServicemethod;
            this.gisService.userName = this.gisServiceusername;
            this.gisService.password = this.gisServicepassword;
            this.gisService.authParams = this.gisServiceauth;
        } else {
            this.gisService = {
                serviceType: this.gisServicetype,
                url: this.gisServiceurl,
                appId: this.gisServiceappid,
                authURL: this.gisServiceAuthurl,
                authparam: this.gisServiceauth,
                customerId: this.customerId,
                authParams: this.gisServiceauth,
                authMethod: this.gisServicemethod,
                userName: this.gisServiceusername,
                password: this.gisServicepassword,
            };
        }
        if (this.editMode) {
            this.GISService.updateGISService(this.gisService).subscribe(() => {
                // #23237 TODO: workaround for GET after POST return HTTP 204 NO CONTENT
                setTimeout(() => {
                    this.close(true);
                }, 1000);
            });
        } else {
            this.GISService.addGISService(this.gisService).subscribe(() => {
                // #23237 TODO:workaround for GET after POST return HTTP 204 NO CONTENT
                setTimeout(() => {
                    this.close(true);
                }, 1000);
            });
        }
    }

    public authMethodChange(event: AuthMethod) {
        this.gisServicemethod = event.name;
        this.domOperationUtilsService.gisDialogAuthMethod.next(this.gisServicemethod);
    }

    ngOnDestroy(): void {
        for(const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
        this.subscriptions = [];
    }
}
